/**
 * StickyContactTray — LinkedIn-style conversation list tray (desktop only)
 *
 * Simplified version of DesktopContactList for the sticky chat widget.
 * Fixed to bottom-right, 300px wide, minimizable to just the header bar.
 * Header matches LinkedIn: user avatar + "Messaging" + action buttons.
 */

import { useState, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import http from '@/network/http';
import { useAuthStore } from '@/features/auth';
import {
  useConversations,
  useArchivedConversations,
  useCreateConversation,
  useTotalUnreadCount,
  useMarkConversationUnread,
  useMuteConversation,
  useArchiveConversation,
  useLeaveConversation,
  useDeleteConversation,
  getConversationDisplayName,
  getConversationAvatar,
} from '../hooks';
import { useBatchPresence } from '../hooks';
import { ChatAvatar } from './ChatAvatar';
import { ConversationActionMenu } from './ConversationActionMenu';
import { ChatSettingsPanel } from './ChatSettingsPanel';
import { useChatWidgetStore } from '../store';
import type { Conversation } from '../types';

interface UserResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  avatarUrl?: string | null;
}

type ContactItem =
  | { kind: 'conversation'; conversation: Conversation; isOnline: boolean }
  | { kind: 'user'; user: UserResult; isOnline: boolean };

// =============================================================================
// Contact Row
// =============================================================================

interface ContactRowProps {
  item: ContactItem;
  currentUserId: string;
  onClick: () => void;
  onMenuClick?: (e: React.MouseEvent) => void;
}

const ContactRow = memo(function ContactRow({
  item,
  currentUserId,
  onClick,
  onMenuClick,
}: ContactRowProps) {
  const { i18n } = useTranslation();

  if (item.kind === 'conversation') {
    const conv = item.conversation;
    const name = getConversationDisplayName(conv, currentUserId);
    const avatar = getConversationAvatar(conv, currentUserId);
    const hasUnread = conv.unreadCount > 0;

    const timeLabel = (() => {
      if (!conv.lastMessageAt) return '';
      const date = new Date(conv.lastMessageAt);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const isHe = i18n.language === 'he';

      if (diff < 24 * 60 * 60 * 1000) {
        return date.toLocaleTimeString(isHe ? 'he-IL' : 'en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      }
      if (diff < 7 * 24 * 60 * 60 * 1000) {
        return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', { weekday: 'short' });
      }
      return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', {
        month: 'short',
        day: 'numeric',
      });
    })();

    const lastMessagePreview = (() => {
      if (!conv.lastMessage) return '';
      const lm = conv.lastMessage;
      if (lm.type !== 'text') return `[${lm.type}]`;
      return lm.content || '';
    })();

    return (
      <div
        className="group relative mx-3"
        style={{
          borderBottom: '1px solid transparent',
          borderImage: 'linear-gradient(to right, transparent 0%, transparent 40px, rgba(148,163,184,0.4) 65px, rgba(148,163,184,0.4) 100%) 1',
        }}
      >
        <button
          type="button"
          onClick={onClick}
          className="flex w-full items-center gap-3 py-2 text-start cursor-pointer transition-colors hover:bg-slate-50"
        >
          {/* Avatar with online indicator */}
          <div className="relative flex-shrink-0">
            <ChatAvatar
              src={avatar}
              name={conv.type === 'channel' ? '#' : name}
              size="h-[50px] w-[50px]"
            />
            {item.isOnline && (
              <div className="absolute -bottom-0.5 -end-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-success-500" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden" dir="auto">
            <div className="flex items-center justify-between gap-1">
              <span
                className={`truncate text-sm ${
                  hasUnread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'
                }`}
              >
                {name}
              </span>
              <span className="flex items-center gap-1 flex-shrink-0 group-hover:hidden">
                {/* Muted icon */}
                {conv.isMuted && (
                  <svg className="h-3 w-3 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18.84 13a6 6 0 00-8.69-8.69L18.84 13zM1 1l22 22" />
                  </svg>
                )}
                <span className="text-xs font-medium tabular-nums text-slate-400">
                  {timeLabel}
                </span>
              </span>
            </div>
            <div className="mt-0.5 flex items-center justify-between gap-1">
              <span className="truncate text-xs font-medium text-slate-400">
                {conv.lastMessage && conv.type !== 'dm' && (
                  <span className="font-medium text-slate-500">{conv.lastMessage.senderName}: </span>
                )}
                {lastMessagePreview}
              </span>
              {hasUnread && (
                <span className="flex h-4 min-w-[16px] flex-shrink-0 items-center justify-center rounded-full bg-slate-700 px-1 text-[9px] font-medium text-white">
                  {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                </span>
              )}
            </div>
          </div>
        </button>

        {/* Hover ··· menu button */}
        {onMenuClick && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMenuClick(e); }}
            className="absolute end-2 top-2 hidden h-6 w-6 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm ring-1 ring-slate-200 transition-colors hover:bg-slate-50 hover:text-slate-600 group-hover:flex"
          >
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  // kind === 'user'
  const { user, isOnline } = item;
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 mx-3 py-2 text-start cursor-pointer transition-colors hover:bg-slate-50"
      style={{
        width: 'calc(100% - 1.5rem)',
        borderBottom: '1px solid transparent',
        borderImage: 'linear-gradient(to right, transparent 0%, transparent 40px, rgba(148,163,184,0.4) 65px, rgba(148,163,184,0.4) 100%) 1',
      }}
    >
      <div className="relative flex-shrink-0">
        <ChatAvatar src={user.avatarUrl} name={fullName} size="h-[50px] w-[50px]" />
        {isOnline && (
          <div className="absolute -bottom-0.5 -end-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-success-500" />
        )}
      </div>
      <div className="flex-1 overflow-hidden" dir="auto">
        <span className="truncate text-sm font-medium text-slate-700">{fullName}</span>
      </div>
    </button>
  );
});

// =============================================================================
// Main Component
// =============================================================================

export function StickyContactTray() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const currentUserId = user?.id || '';
  const currentUserName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '';
  const currentUserAvatar = user?.avatarUrl || null;
  const { isTrayMinimized, minimizeTray, openTray, openChat, openNewMessage } = useChatWidgetStore();
  const unreadCount = useTotalUnreadCount();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'chats' | 'contacts'>('chats');
  const [trayMenuState, setTrayMenuState] = useState<{
    conversationId: string;
    isMuted: boolean;
    position: { x: number; y: number };
  } | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const [showArchivedView, setShowArchivedView] = useState(false);

  // Action hooks
  const markUnread = useMarkConversationUnread();
  const muteConversation = useMuteConversation();
  const archiveConversation = useArchiveConversation();
  const leaveConversation = useLeaveConversation();
  const deleteConversation = useDeleteConversation();

  // Data
  const { data: conversations } = useConversations();
  const { data: archivedConversations } = useArchivedConversations();
  const { data: usersData } = useQuery({
    queryKey: ['messaging', 'users'],
    queryFn: () =>
      http.get<{ users: UserResult[] }>('/messaging/users').then((r) => r.data),
    staleTime: 30 * 1000,
  });

  const allUsers = usersData?.users || [];
  const filteredSystemUsers = useMemo(
    () => allUsers.filter((u) => u.id !== currentUserId),
    [allUsers, currentUserId],
  );

  // Batch presence
  const allUserIds = useMemo(() => {
    const ids = new Set<string>();
    filteredSystemUsers.forEach((u) => ids.add(u.id));
    conversations?.forEach((c) => {
      c.participants.forEach((p) => {
        if (p.userId !== currentUserId) ids.add(p.userId);
      });
    });
    return Array.from(ids);
  }, [filteredSystemUsers, conversations, currentUserId]);

  const { presenceMap } = useBatchPresence(allUserIds);
  const createConversation = useCreateConversation();

  // Build separate lists for Chats and Contacts tabs
  const { chatItems, contactOnlyItems } = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const chats: ContactItem[] = [];
    const contacts: ContactItem[] = [];
    const usersWithConversations = new Set<string>();
    const convList = conversations || [];

    convList.forEach((conv) => {
      if (conv.type === 'dm') {
        conv.participants.forEach((p) => {
          if (p.userId !== currentUserId) usersWithConversations.add(p.userId);
        });
      }
    });

    // Conversations with messages → Chats tab (sorted by recency)
    const conversationsWithMessages = convList
      .filter((c) => {
        if (!c.lastMessageAt) return false;
        if (!q) return true;
        return getConversationDisplayName(c, currentUserId).toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return bTime - aTime;
      });

    conversationsWithMessages.forEach((conv) => {
      const dmPartnerId = conv.type === 'dm'
        ? conv.participants.find((p) => p.userId !== currentUserId)?.userId
        : null;
      chats.push({
        kind: 'conversation',
        conversation: conv,
        isOnline: dmPartnerId ? !!presenceMap[dmPartnerId]?.isOnline : false,
      });
    });

    // Empty conversations → Contacts tab
    convList
      .filter((c) => {
        if (c.lastMessageAt) return false;
        if (!q) return true;
        return getConversationDisplayName(c, currentUserId).toLowerCase().includes(q);
      })
      .forEach((conv) => {
        const dmPartnerId = conv.type === 'dm'
          ? conv.participants.find((p) => p.userId !== currentUserId)?.userId
          : null;
        contacts.push({
          kind: 'conversation',
          conversation: conv,
          isOnline: dmPartnerId ? !!presenceMap[dmPartnerId]?.isOnline : false,
        });
      });

    // Users without conversations → Contacts tab
    const usersWithout = filteredSystemUsers.filter((u) => {
      if (usersWithConversations.has(u.id)) return false;
      if (!q) return true;
      return [u.firstName, u.lastName].join(' ').toLowerCase().includes(q);
    });

    // Online first, then offline — both alphabetical
    const onlineUsers = usersWithout
      .filter((u) => presenceMap[u.id]?.isOnline)
      .sort((a, b) => [a.firstName, a.lastName].join(' ').localeCompare([b.firstName, b.lastName].join(' ')));

    onlineUsers.forEach((user) => contacts.push({ kind: 'user', user, isOnline: true }));

    const offlineUsers = usersWithout
      .filter((u) => !presenceMap[u.id]?.isOnline)
      .sort((a, b) => [a.firstName, a.lastName].join(' ').localeCompare([b.firstName, b.lastName].join(' ')));

    offlineUsers.forEach((user) => contacts.push({ kind: 'user', user, isOnline: false }));

    return { chatItems: chats, contactOnlyItems: contacts };
  }, [conversations, filteredSystemUsers, presenceMap, searchQuery, currentUserId]);

  const displayItems = activeTab === 'chats' ? chatItems : contactOnlyItems;

  // Handlers
  const handleConversationClick = useCallback(
    (conversationId: string) => {
      openChat(conversationId);
    },
    [openChat],
  );

  const handleUserClick = useCallback(
    async (user: UserResult) => {
      try {
        const result = await createConversation.mutateAsync({
          type: 'dm',
          participantIds: [user.id],
        });
        openChat(result.conversation.id);
      } catch (error) {
        console.error('Failed to create DM:', error);
      }
    },
    [createConversation, openChat],
  );

  const handleCompose = useCallback(() => {
    openNewMessage();
  }, [openNewMessage]);

  return (
    <div
      className={`flex w-[300px] flex-col overflow-hidden rounded-t-xl border border-slate-200 bg-white shadow-lg transition-[max-height] duration-300 ease-out ${
        isTrayMinimized ? 'max-h-[52px]' : 'max-h-[calc(100dvh-16px)]'
      }`}
    >
      {/* Header — LinkedIn style: avatar + title + action buttons */}
      <button
        type="button"
        onClick={isTrayMinimized ? openTray : minimizeTray}
        className="flex h-[52px] flex-shrink-0 items-center gap-2.5 border-b border-slate-100 px-3 hover:bg-slate-50"
      >
        {/* Current user avatar with online dot */}
        <div className="relative flex-shrink-0">
          <ChatAvatar src={currentUserAvatar} name={currentUserName} size="h-8 w-8" />
          <div className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full border-2 border-white bg-success-500" />
        </div>

        {/* Title + unread badge */}
        <span className="text-base font-semibold text-slate-900">{t('messaging.title')}</span>
        {unreadCount > 0 && (
          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-error-500 px-1.5 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}

        <div className="flex-1" />

        {/* Action buttons (stop propagation so they don't toggle tray) */}

        {/* Settings (gear) menu */}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); setIsSettingsOpen(!isSettingsOpen); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setIsSettingsOpen(!isSettingsOpen); } }}
          className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </span>

        {/* Compose / new message */}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); handleCompose(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleCompose(); } }}
          className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100"
          title={t('messaging.newConversation')}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </span>

        {/* Minimize / expand chevron */}
        <span
          className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${
            isTrayMinimized
              ? 'text-slate-500 hover:bg-slate-100'
              : 'bg-slate-100 text-slate-700'
          }`}
        >
          <svg
            className={`h-4 w-4 transition-transform duration-200 ${isTrayMinimized ? '' : 'rotate-180'}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </span>
      </button>

      {/* Body — hidden when minimized via overflow:hidden + height transition */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Search */}
        <div className="px-3 pt-2 pb-1">
          <div className="relative">
            <svg
              className="pointer-events-none absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('messaging.searchContacts')}
              dir="auto"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pe-3 ps-8 text-xs text-slate-900 placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:outline-none"
            />
          </div>
        </div>

        {/* Tabs — Chats | Contacts */}
        <div className="flex border-b border-slate-100 px-3">
          {(['chats', 'contacts'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`relative flex-1 py-2 text-xs font-semibold transition-colors ${
                activeTab === tab
                  ? 'text-slate-900'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {t(`messaging.${tab}`)}
              {activeTab === tab && (
                <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-slate-700" />
              )}
            </button>
          ))}
        </div>

        {/* Contact list */}
        <div className="flex-1 overflow-y-auto">
          {displayItems.map((item) => {
            const key =
              item.kind === 'conversation'
                ? `conv-${item.conversation.id}`
                : `user-${item.user.id}`;
            return (
              <ContactRow
                key={key}
                item={item}
                currentUserId={currentUserId}
                onClick={
                  item.kind === 'conversation'
                    ? () => handleConversationClick(item.conversation.id)
                    : () => handleUserClick(item.user)
                }
                onMenuClick={
                  activeTab === 'chats' && item.kind === 'conversation'
                    ? (e: React.MouseEvent) => {
                        const rect = (e.target as HTMLElement).getBoundingClientRect();
                        setTrayMenuState({
                          conversationId: item.conversation.id,
                          isMuted: item.conversation.isMuted,
                          position: { x: rect.left, y: rect.bottom + 4 },
                        });
                      }
                    : undefined
                }
              />
            );
          })}

          {displayItems.length === 0 && !showArchivedView && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <svg className="mb-2 h-6 w-6 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
              <p className="text-xs text-slate-400">
                {searchQuery ? t('messaging.noResults') : t('messaging.noConversations')}
              </p>
            </div>
          )}

          {/* Archived view */}
          {showArchivedView && (
            <>
              <button
                type="button"
                onClick={() => setShowArchivedView(false)}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50"
              >
                <svg className="h-3.5 w-3.5 rtl:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                {t('messaging.title')}
              </button>
              {archivedConversations && archivedConversations.length > 0 ? (
                archivedConversations.map((conv) => {
                  const dmPartnerId = conv.type === 'dm'
                    ? conv.participants.find((p) => p.userId !== currentUserId)?.userId
                    : null;
                  return (
                    <ContactRow
                      key={`archived-${conv.id}`}
                      item={{
                        kind: 'conversation',
                        conversation: conv,
                        isOnline: dmPartnerId ? !!presenceMap[dmPartnerId]?.isOnline : false,
                      }}
                      currentUserId={currentUserId}
                      onClick={() => handleConversationClick(conv.id)}
                      onMenuClick={(e: React.MouseEvent) => {
                        const rect = (e.target as HTMLElement).getBoundingClientRect();
                        setTrayMenuState({
                          conversationId: conv.id,
                          isMuted: conv.isMuted,
                          position: { x: rect.left, y: rect.bottom + 4 },
                        });
                      }}
                    />
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <svg className="mb-2 h-5 w-5 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="21 8 21 21 3 21 3 8" />
                    <rect x="1" y="3" width="22" height="5" />
                  </svg>
                  <p className="text-xs text-slate-400">{t('messaging.noArchivedConversations')}</p>
                </div>
              )}
            </>
          )}

          {/* "Archived" link at bottom — only on chats tab */}
          {!showArchivedView && activeTab === 'chats' && (
            <button
              type="button"
              onClick={() => setShowArchivedView(true)}
              className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-50"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="21 8 21 21 3 21 3 8" />
                <rect x="1" y="3" width="22" height="5" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
              {t('messaging.archived')}
            </button>
          )}
        </div>
      </div>

      {/* Tray row action menu */}
      {trayMenuState && (
        <ConversationActionMenu
          conversationId={trayMenuState.conversationId}
          isMuted={trayMenuState.isMuted}
          position={trayMenuState.position}
          onClose={() => setTrayMenuState(null)}
          onMarkUnread={() => {
            markUnread(trayMenuState.conversationId);
            setTrayMenuState(null);
          }}
          onToggleMute={() => {
            muteConversation.mutate({
              conversationId: trayMenuState.conversationId,
              isMuted: !trayMenuState.isMuted,
            });
            setTrayMenuState(null);
          }}
          onToggleArchive={() => {
            archiveConversation.mutate({
              conversationId: trayMenuState.conversationId,
              isArchived: true,
            });
            setTrayMenuState(null);
          }}
          onLeave={() => {
            if (window.confirm(t('messaging.leaveConfirm'))) {
              leaveConversation.mutate(trayMenuState.conversationId);
            }
            setTrayMenuState(null);
          }}
          onDelete={() => {
            if (window.confirm(t('messaging.deleteConversationConfirm'))) {
              deleteConversation.mutate(trayMenuState.conversationId);
            }
            setTrayMenuState(null);
          }}
        />
      )}

      {/* Settings panel overlay */}
      <AnimatePresence>
        {isSettingsOpen && (
          <div className="absolute bottom-full mb-2 end-0">
            <ChatSettingsPanel onClose={() => setIsSettingsOpen(false)} />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
